/**
 * 首页「我的农场」预览（与 weixinprogram/pages/home/home.js 同源逻辑）
 */

export const DEMO_FARM_ID = 'demo_farm';
export const DEFAULT_HOME_FARM_COVER = '/images/swiper/home-swiper-1.png';

export function buildDemoLandParcel() {
  return {
    id: 'demo_land_parcel',
    isDemo: true,
    farmId: DEMO_FARM_ID,
    farmName: '演示农场',
    parcelName: '一号示范地块',
    quantity: 12.5,
    currentCrop: '小麦'
  };
}

export function fmtMu(mu) {
  if (!Number.isFinite(mu)) return '';
  if (mu >= 100000) return `${Math.round(mu / 10000) / 10}万亩`;
  if (mu >= 10000) return `${Math.round(mu / 10000)}万亩`;
  return `${Math.round(mu)}亩`;
}

function farmsReal(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.filter((f) => f && f.id != null && String(f.id).trim() !== '');
}

function uniqCropsFromPlots(plots, maxLen) {
  const out = [];
  const seen = new Set();
  (plots || []).forEach((pl) => {
    const c = String((pl && (pl.currentCrop || pl.current_crop)) || '').trim();
    if (!c || seen.has(c)) return;
    seen.add(c);
    out.push(c);
    if (out.length >= maxLen) return;
  });
  return out.slice(0, maxLen);
}

function primaryAreaQty(farm, plots) {
  const sumQty = (plots || []).reduce((s, pl) => {
    const n = Number(pl && pl.quantity);
    return s + (Number.isFinite(n) ? n : 0);
  }, 0);
  const ta =
    farm && farm.totalArea != null && Number(farm.totalArea) > 0
      ? Number(farm.totalArea)
      : sumQty > 0
        ? sumQty
        : null;
  return ta != null && Number.isFinite(ta) && ta > 0 ? ta : null;
}

function landRowsFromBody(landBody) {
  if (!landBody || !landBody.success) return [];
  if (Array.isArray(landBody.data)) return landBody.data;
  if (landBody.data && Array.isArray(landBody.data.content)) return landBody.data.content;
  if (Array.isArray(landBody.content)) return landBody.content;
  return [];
}

/** 农场嵌套 plots 常为空：用 /land-parcels 按 farmId 补齐面积与作物 */
function plotsForFarm(farm, landRows) {
  const nested = Array.isArray(farm && farm.plots) ? farm.plots.slice() : [];
  const farmId = farm && farm.id != null ? String(farm.id).trim() : '';
  if (!farmId || !Array.isArray(landRows) || !landRows.length) return nested;
  const fromLand = landRows.filter((p) => {
    const fid = String((p && (p.farmId != null ? p.farmId : p.farm_id)) || '').trim();
    return fid && fid === farmId;
  });
  if (!fromLand.length) return nested;
  if (!nested.length) return fromLand;
  const seen = new Set(
    nested
      .map((p) => String((p && (p.id != null ? p.id : p.landParcelId)) || '').trim())
      .filter(Boolean)
  );
  fromLand.forEach((p) => {
    const id = String((p && (p.id != null ? p.id : p.landParcelId)) || '').trim();
    if (id && seen.has(id)) return;
    if (id) seen.add(id);
    nested.push(p);
  });
  return nested;
}

function buildHeroMetaLine(areaMu, plotCount, crops) {
  const metaParts = [];
  const areaText = areaMu != null ? fmtMu(areaMu) : '';
  if (areaText) metaParts.push(areaText);
  const n = Number(plotCount);
  if (Number.isFinite(n) && n > 0) metaParts.push(`${n}块地`);
  if (Array.isArray(crops) && crops.length) metaParts.push(crops.join(' · '));
  return metaParts.length ? metaParts.join(' | ') : '暂无面积与作物信息';
}

function deviceSummaryFromBody(deviceBody) {
  const body = deviceBody && typeof deviceBody === 'object' ? deviceBody : null;
  const data =
    body && body.success && body.data && typeof body.data === 'object'
      ? body.data
      : body && typeof body.total === 'number'
        ? body
        : null;
  if (!data) return { total: 0, online: 0 };
  return {
    total: Number(data.total) || 0,
    online: Number(data.online) || 0
  };
}

function countOpenFieldTasks(tasksBody) {
  if (!tasksBody || !tasksBody.success || !Array.isArray(tasksBody.data)) return 0;
  return tasksBody.data.reduce((sum, t) => {
    const st = String((t && t.status) || '').trim();
    if (!st || st === '已完成' || st === 'COMPLETED' || st === 'CANCELLED' || st === '已取消') {
      return sum;
    }
    return sum + 1;
  }, 0);
}

function plantingPlansFromBody(planBody) {
  if (!planBody || !planBody.success) return [];
  if (Array.isArray(planBody.content)) return planBody.content;
  if (Array.isArray(planBody.data)) return planBody.data;
  if (planBody.data && Array.isArray(planBody.data.content)) return planBody.data.content;
  return [];
}

function parseStageNotes(raw) {
  if (!raw || typeof raw !== 'string') return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.stages) ? parsed.stages : [];
  } catch {
    return [];
  }
}

function planNotesForStages(plan) {
  const n = typeof plan?.notes === 'string' ? plan.notes.trim() : '';
  const r = typeof plan?.remark === 'string' ? plan.remark.trim() : '';
  if (n && parseStageNotes(n).length) return n;
  if (r && parseStageNotes(r).length) return r;
  return n || r || '';
}

function isSubPlantingPlan(plan) {
  if (plan?.parentPlanId) return true;
  const notes = typeof plan?.notes === 'string' ? plan.notes : '';
  if (!notes) return false;
  try {
    const parsed = JSON.parse(notes);
    return Boolean(parsed?.parentPlanId);
  } catch {
    return false;
  }
}

function parseDateOnly(v) {
  if (!v) return null;
  const s = String(v).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function dayDiff(from, to) {
  if (!from || !to) return null;
  return Math.floor((to.getTime() - from.getTime()) / 86400000);
}

function resolvePlanStageName(stages, elapsedDays, fallback) {
  if (Array.isArray(stages) && stages.length) {
    const matched = stages.find((s) => {
      const start = Number(s.startDay || 1) - 1;
      const end = Number(s.endDay || 0);
      return elapsedDays >= start && elapsedDays < end;
    });
    const pick = (s) => String(s?.name || s?.stageName || '').trim();
    if (matched) return pick(matched);
    const last = stages[stages.length - 1];
    if (elapsedDays >= Number(last.endDay || 0)) return pick(last);
  }
  return String(fallback || '').trim();
}

function summarizeFarmPlanting(plans, farmId, landRows) {
  const fid = String(farmId || '').trim();
  const empty = {
    crops: [],
    areaMu: null,
    showStageProgress: false,
    stagePrefix: '当前阶段：',
    stageHighlight: '',
    progressPct: 0,
    hasPlans: false
  };
  if (!fid) return empty;

  const farmLandIds = new Set();
  const landAreaById = new Map();
  (landRows || []).forEach((p) => {
    const landFarmId = String(p?.farmId ?? p?.farm_id ?? '').trim();
    const id = String(p?.id ?? p?.landParcelId ?? '').trim();
    if (!id || landFarmId !== fid) return;
    farmLandIds.add(id);
    const q = Number(p?.quantity);
    if (Number.isFinite(q) && q > 0) landAreaById.set(id, q);
  });

  const related = (plans || []).filter((p) => {
    if (!p || isSubPlantingPlan(p)) return false;
    const lid = String(p.landId || p.fieldId || p.land_id || '').trim();
    const pfid = String(p.farmId || p.farm_id || '').trim();
    if (lid && farmLandIds.has(lid)) return true;
    if (pfid && pfid === fid) return true;
    return false;
  });
  if (!related.length) return empty;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const enriched = related.map((plan) => {
    const start = parseDateOnly(plan.startDate || plan.planStartDate || plan.actualStartDate);
    const end = parseDateOnly(plan.endDate || plan.planEndDate || plan.actualEndDate);
    const elapsed = start ? dayDiff(start, today) : null;
    const total = start && end ? dayDiff(start, end) : null;
    const st = String(plan.status || '').trim();
    let scope = 'other';
    if (st === '已完成' || st === 'completed' || st === '暂停' || st === '取消') {
      scope = 'past';
    } else if (start && elapsed != null) {
      if (elapsed < 0) scope = 'future';
      else if (total != null && total >= 0 && elapsed > total) scope = 'past';
      else scope = 'current';
    } else if (st === '进行中' || st === 'in_progress') {
      scope = 'current';
    }
    return { plan, start, end, elapsed, total, scope };
  });

  const current = enriched.filter((x) => x.scope === 'current');
  const pool = current.length ? current : enriched;

  const crops = [];
  const cropSeen = new Set();
  pool.forEach(({ plan }) => {
    const c = String(plan.cropName || plan.crop_name || '').trim();
    if (!c || cropSeen.has(c)) return;
    cropSeen.add(c);
    crops.push(c);
  });

  let areaSum = 0;
  const areaSeen = new Set();
  pool.forEach(({ plan }) => {
    const lid = String(plan.landId || plan.fieldId || plan.land_id || '').trim();
    if (lid) {
      if (areaSeen.has(lid)) return;
      areaSeen.add(lid);
      if (landAreaById.has(lid)) {
        areaSum += landAreaById.get(lid);
        return;
      }
    }
    const a = Number(plan.area != null ? plan.area : plan.plannedArea);
    if (Number.isFinite(a) && a > 0) areaSum += a;
  });

  const stageCandidate =
    current[0] ||
    pool.find((x) => parseStageNotes(planNotesForStages(x.plan)).length > 0) ||
    null;

  let showStageProgress = false;
  let stageHighlight = '';
  let progressPct = 0;
  const stagePrefix = '当前阶段：';

  if (stageCandidate) {
    const stages = parseStageNotes(planNotesForStages(stageCandidate.plan));
    const elapsed = stageCandidate.elapsed != null ? stageCandidate.elapsed : 0;
    let totalDays = stageCandidate.total;
    if (!(totalDays > 0) && stages.length) {
      totalDays = Number(stages[stages.length - 1].endDay) || 0;
    }
    const stageName = resolvePlanStageName(
      stages,
      Math.max(0, elapsed),
      stageCandidate.plan.growthStage || stageCandidate.plan.currentStage || stageCandidate.plan.stage
    );
    if (stageName || (totalDays > 0 && stageCandidate.start)) {
      showStageProgress = true;
      stageHighlight =
        stageName || (totalDays > 0 ? `已运行 ${Math.max(0, elapsed)} / ${totalDays} 天` : '进行中');
      progressPct =
        totalDays > 0
          ? Math.max(0, Math.min(100, Math.round((100 * Math.max(0, elapsed)) / totalDays)))
          : 0;
    }
  }

  return {
    crops,
    areaMu: areaSum > 0 ? areaSum : null,
    showStageProgress,
    stagePrefix,
    stageHighlight,
    progressPct,
    hasPlans: true
  };
}

function preferFarmWithPlanting(realFarms, plans, landRows) {
  const rows = Array.isArray(realFarms) ? realFarms.slice() : [];
  if (rows.length <= 1) return rows;
  const score = (farm) => {
    const s = summarizeFarmPlanting(plans, farm?.id, landRows);
    if (s.showStageProgress) return 3;
    if (s.crops.length || s.areaMu != null) return 2;
    if (s.hasPlans) return 1;
    return 0;
  };
  rows.sort((a, b) => score(b) - score(a));
  return rows;
}

export function buildHomeDemoFarmHero() {
  const demoLp = buildDemoLandParcel();
  const areaMu = Number(demoLp.quantity);
  const crop = String(demoLp.currentCrop || '').trim();
  const crops = crop ? [crop] : [];
  const metaLine = buildHeroMetaLine(
    Number.isFinite(areaMu) && areaMu > 0 ? areaMu : null,
    1,
    crops
  );
  return {
    farmId: DEMO_FARM_ID,
    name: '演示农场',
    isDemo: true,
    showBadge: true,
    badgeText: '演示',
    coverUrl: DEFAULT_HOME_FARM_COVER,
    metaLine: metaLine === '暂无面积与作物信息' ? '演示数据' : metaLine,
    showStageProgress: true,
    stagePrefix: '当前阶段：',
    stageHighlight: crop ? `${crop} · 演示预览` : '演示预览',
    progressPct: 28
  };
}

export function buildHomeHeroFromLandRows(landRows, planting) {
  const rows = Array.isArray(landRows) ? landRows : [];
  if (!rows.length) return null;
  const first = rows[0] || {};
  const name = String(first.farmName || first.farm_name || '我的农场').trim() || '我的农场';
  const farmId = String(first.farmId || first.farm_id || '');
  const plant = planting || summarizeFarmPlanting([], farmId, rows);
  const areaMu =
    plant.areaMu != null
      ? plant.areaMu
      : rows.reduce((sum, p) => {
          const n = Number(p && p.quantity);
          return sum + (Number.isFinite(n) ? n : 0);
        }, 0);
  const crops = plant.crops.length ? plant.crops : uniqCropsFromPlots(rows, 4);
  const metaLine = buildHeroMetaLine(areaMu > 0 ? areaMu : null, rows.length, crops);
  return {
    farmId,
    name,
    showBadge: true,
    badgeText: '主农场',
    coverUrl: DEFAULT_HOME_FARM_COVER,
    metaLine: metaLine === '暂无面积与作物信息' ? '已有关联地块' : metaLine,
    location: String(first.location || first.farmLocation || '').trim(),
    showStageProgress: !!plant.showStageProgress,
    stagePrefix: plant.stagePrefix || '当前阶段：',
    stageHighlight: plant.stageHighlight || '',
    progressPct: plant.progressPct || 0
  };
}

export function getFullImageUrl(url) {
  if (!url || typeof url !== 'string') return '';
  const s = url.trim();
  const localhost8088 = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\//;
  if (localhost8088.test(s)) {
    const path = s.replace(localhost8088, '/');
    return path.startsWith('/') ? path : `/${path}`;
  }
  if (s.startsWith('http://') || s.startsWith('https://')) return s;
  return s.startsWith('/') ? s : `/${s}`;
}

function resolveFarmCoverUrl(primary) {
  const rawCover =
    (primary.coverImageUrl ||
      primary.coverUrl ||
      primary.cover ||
      primary.imageUrl ||
      primary.image_url ||
      '') + '';
  const resolvedFromApi = typeof rawCover === 'string' && rawCover.trim() ? getFullImageUrl(rawCover.trim()) : '';
  return resolvedFromApi || DEFAULT_HOME_FARM_COVER;
}

function buildFarmSlideFromRow(primary, progressPct, badgeText, landRows) {
  const plots = plotsForFarm(primary, landRows);
  const name = String(primary.farmName || primary.farm_name || primary.name || '未命名农场').trim() || '未命名农场';
  const areaMu = primaryAreaQty(primary, plots);
  const crops = uniqCropsFromPlots(plots, 1);
  const plotCount =
    typeof primary.plotCount === 'number' && primary.plotCount > 0 ? primary.plotCount : plots.length;
  return {
    farmId: String(primary.id || ''),
    badgeText: badgeText || '主农场',
    name,
    status: '运行正常',
    area: areaMu != null ? fmtMu(areaMu) : '—',
    crop: crops[0] || '—',
    plots: plotCount > 0 ? `${plotCount}块` : '—',
    progress: progressPct,
    coverUrl: resolveFarmCoverUrl(primary),
  };
}

export function heroToFarmSlide(hero) {
  if (!hero) return null;
  const metaParts = String(hero.metaLine || '').split('|').map((s) => s.trim()).filter(Boolean);
  return {
    farmId: hero.farmId || '',
    badgeText: hero.badgeText || '主农场',
    name: hero.name || '我的农场',
    status: '运行正常',
    area: metaParts.find((p) => p.includes('亩')) || '—',
    crop: metaParts.find((p) => !p.includes('亩') && !/^\d+块/.test(p)) || '—',
    plots: metaParts.find((p) => /^\d+块/.test(p)) || '—',
    progress: hero.progressPct || 0,
    coverUrl: hero.coverUrl || DEFAULT_HOME_FARM_COVER,
  };
}

function buildFarmSlidesFromReal(real, progressPct, landRows) {
  if (!real.length) return [];
  return real.map((row, index) =>
    buildFarmSlideFromRow(row, progressPct, index === 0 ? '主农场' : '分农场', landRows)
  );
}

const DEMO_KPIS = [
  { key: 'plots', label: '地块数量', value: '0块', foot: '演示预览', tone: 'muted' },
  { key: 'devices', label: '设备数量', value: '0台', foot: '演示预览', tone: 'muted' },
  { key: 'online', label: '在线设备', value: '0台', foot: '演示预览', tone: 'muted' },
  { key: 'tasks', label: '农事任务', value: '0条', foot: '演示预览', tone: 'muted' },
  { key: 'risk', label: '异常告警', value: '0条', foot: '暂无', tone: 'ok' },
  { key: 'farms', label: '农场数', value: '0个', foot: '演示预览', tone: 'muted' }
];

const EMPTY_KPIS = [
  { key: 'plots', label: '地块数量', value: '0块', foot: '待完善', tone: 'muted' },
  { key: 'devices', label: '设备数量', value: '0台', foot: '待接入', tone: 'muted' },
  { key: 'online', label: '在线设备', value: '0台', foot: '待接入', tone: 'muted' },
  { key: 'tasks', label: '农事任务', value: '0条', foot: '暂无', tone: 'muted' },
  { key: 'risk', label: '异常告警', value: '0条', foot: '暂无', tone: 'ok' },
  { key: 'farms', label: '农场数', value: '0个', foot: '去新建', tone: 'muted' }
];

export function buildEmptyHomeFarmPreview() {
  return {
    hero: null,
    kpis: EMPTY_KPIS,
    showDemoFarmPreview: false,
    farmSlides: [],
    preferredFarmId: '',
    farmOptions: [],
    counts: {
      farmCount: 0,
      plotTotal: 0,
      warnN: 0,
      cropTotal: 0,
      deviceTotal: 0,
      devicesOnline: 0,
      taskOpenN: 0
    },
  };
}

/**
 * 根据与小程序相同的接口响应体计算预览（farmsBody 等为 { success, data } 形态）
 * @param {{ preferredFarmId?: string }} [opts]
 */
export function computeHomeFarmPreviewFromBodies({
  farmsBody,
  statsBody,
  warnBody,
  landBody,
  deviceBody,
  tasksBody,
  planBody,
  preferredFarmId = '',
}) {
  const raw = farmsBody && farmsBody.success && Array.isArray(farmsBody.data) ? farmsBody.data : [];
  let real = farmsReal(raw);

  const landRows = landRowsFromBody(landBody);
  const planRows = plantingPlansFromBody(planBody);

  const pref = preferredFarmId != null ? String(preferredFarmId).trim() : '';
  if (pref && real.length > 1) {
    const idx = real.findIndex((f) => String(f?.id) === pref);
    if (idx > 0) {
      const next = real.slice();
      const [picked] = next.splice(idx, 1);
      real = [picked, ...next];
    }
  } else if (!pref) {
    real = preferFarmWithPlanting(real, planRows, landRows);
  }

  let farmCount = real.length;
  let plotTotal = real.reduce((sum, f) => {
    const plots = plotsForFarm(f, landRows);
    const n = typeof f.plotCount === 'number' && f.plotCount > 0 ? f.plotCount : plots.length;
    const num = Number(n);
    return sum + (Number.isFinite(num) ? num : 0);
  }, 0);
  if (landRows.length > plotTotal) {
    plotTotal = landRows.length;
  }

  if (farmCount === 0 && landRows.length > 0) {
    const farmKeys = new Set(
      landRows
        .map((p) =>
          String(p && (p.farmId != null ? p.farmId : p.farm_id != null ? p.farm_id : '')).trim()
        )
        .filter(Boolean)
    );
    farmCount = farmKeys.size > 0 ? farmKeys.size : 1;
    plotTotal = landRows.length;
  }

  const stats = statsBody && statsBody.success && statsBody.data ? statsBody.data : {};
  const cropTotal = Number(stats.total) || 0;
  const cropInProg = Number(stats.inProgress) || 0;
  const statsProgressPct =
    cropTotal > 0 ? Math.min(100, Math.round((100 * cropInProg) / cropTotal)) : 0;

  const warnN = warnBody && warnBody.success && Array.isArray(warnBody.data) ? warnBody.data.length : 0;
  const { total: deviceTotal, online: deviceOnline } = deviceSummaryFromBody(deviceBody);
  const taskOpenN = countOpenFieldTasks(tasksBody);

  let hero = null;
  if (real.length) {
    const primary = real[0];
    const plots = plotsForFarm(primary, landRows);
    const planting = summarizeFarmPlanting(planRows, primary.id, landRows);
    const name = String(primary.farmName || primary.farm_name || primary.name || '未命名农场').trim();
    const areaMu = planting.areaMu != null ? planting.areaMu : primaryAreaQty(primary, plots);
    const crops = planting.crops.length ? planting.crops : uniqCropsFromPlots(plots, 4);
    const primaryPlotCount =
      typeof primary.plotCount === 'number' && primary.plotCount > 0
        ? primary.plotCount
        : plots.length;
    const metaLine = buildHeroMetaLine(areaMu, primaryPlotCount, crops);
    const coverUrl = resolveFarmCoverUrl(primary);

    hero = {
      farmId: String(primary.id),
      name,
      isDemo: false,
      showBadge: true,
      badgeText: '主农场',
      coverUrl,
      metaLine,
      location: String(primary.location || '').trim(),
      showStageProgress: !!planting.showStageProgress,
      stagePrefix: planting.stagePrefix || '当前阶段：',
      stageHighlight: planting.stageHighlight || '',
      progressPct: planting.showStageProgress ? planting.progressPct : statsProgressPct
    };
  } else if (landRows.length > 0) {
    const farmIdHint = String(landRows[0]?.farmId ?? landRows[0]?.farm_id ?? '').trim();
    const planting = summarizeFarmPlanting(planRows, farmIdHint, landRows);
    hero = buildHomeHeroFromLandRows(landRows, planting);
  }

  const farmFoot = farmCount ? '已建档' : '去新建';
  const farmTone = farmCount ? 'ok' : 'muted';
  const plotFoot = plotTotal ? '正常' : '待完善';
  const plotTone = plotTotal ? 'ok' : 'muted';
  const deviceFoot = deviceTotal ? '已接入' : '待接入';
  const deviceTone = deviceTotal ? 'ok' : 'muted';
  const onlineFoot = deviceOnline ? '在线' : deviceTotal ? '暂无在线' : '待接入';
  const onlineTone = deviceOnline ? 'ok' : deviceTotal ? 'warn' : 'muted';
  const taskFoot = taskOpenN ? '进行中' : '暂无';
  const taskTone = taskOpenN ? 'ok' : 'muted';
  let riskTone = 'ok';
  if (warnN > 0) {
    riskTone = 'warn';
  }

  const kpis = [
    { key: 'plots', label: '地块数量', value: `${plotTotal}块`, foot: plotFoot, tone: plotTone },
    { key: 'devices', label: '设备数量', value: `${deviceTotal}台`, foot: deviceFoot, tone: deviceTone },
    { key: 'online', label: '在线设备', value: `${deviceOnline}台`, foot: onlineFoot, tone: onlineTone },
    { key: 'tasks', label: '农事任务', value: `${taskOpenN}条`, foot: taskFoot, tone: taskTone },
    {
      key: 'risk',
      label: '异常告警',
      value: `${warnN}条`,
      foot: warnN ? '需关注' : '暂无',
      tone: riskTone
    },
    { key: 'farms', label: '农场数', value: `${farmCount}个`, foot: farmFoot, tone: farmTone }
  ];

  const slideProgress = hero?.showStageProgress ? hero.progressPct : statsProgressPct;
  const farmSlides = buildFarmSlidesFromReal(real, slideProgress, landRows);
  const slides = farmSlides.length > 0
    ? farmSlides
    : hero
      ? [heroToFarmSlide(hero)].filter(Boolean)
      : [];

  return {
    hero,
    kpis,
    showDemoFarmPreview: false,
    farmSlides: slides,
    preferredFarmId: hero?.farmId || pref || '',
    farmOptions: real.map((f) => ({
      id: String(f.id),
      name: String(f.farmName || f.farm_name || f.name || f.id).trim() || String(f.id),
    })),
    counts: {
      farmCount,
      plotTotal,
      warnN,
      cropTotal,
      deviceTotal,
      devicesOnline: deviceOnline,
      taskOpenN
    },
  };
}

export function buildLoggedOutHomeFarmPreview() {
  const hero = buildHomeDemoFarmHero();
  return {
    hero,
    kpis: DEMO_KPIS,
    showDemoFarmPreview: true,
    farmSlides: [heroToFarmSlide(hero)].filter(Boolean),
  };
}

export function buildErrorHomeFarmPreview() {
  return buildEmptyHomeFarmPreview();
}

/** axios 响应 → 小程序 request 业务体 { success, data, content, records } */
export function axiosToMiniBody(axiosRes) {
  if (!axiosRes) return null;
  const body = axiosRes.data;
  if (body && typeof body === 'object') return body;
  return null;
}
